import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../context-state/AuthContext';
import {
  ACTIVITY_DEBOUNCE_MS,
  INACTIVITY_EXCLUDED_ROUTES,
  LAST_ACTIVE_AT_STORAGE_KEY,
} from '../config-global';

// Only log localStorage failure once per page load to avoid flooding logs
let hasLoggedStorageError = false;

/**
 * Hook that tracks the last time an authenticated dashboard user was active,
 * by writing a timestamp to localStorage on user interaction.
 *
 * This supports the browser-close session timeout feature:
 * when the user returns after closing the browser, AuthContext.initialize()
 * reads this timestamp and forces logout if too much time has passed.
 *
 * Excluded routes (scanner pages) are respected — same list as useInactivityLogout.
 * Does NOT check sessionConfig — writing is always on for authenticated non-excluded users.
 * The feature flag is enforced at read time in AuthContext.initialize().
 */
export const useSessionActivity = () => {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();
  const debounceRef = useRef(null);

  const isExcludedRoute = INACTIVITY_EXCLUDED_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  const writeLastActiveAt = useCallback((source = 'activity') => {
    try {
      localStorage.setItem(LAST_ACTIVE_AT_STORAGE_KEY, Date.now().toString());
      console.debug('📍 lastActiveAt written:', source);
    } catch (error) {
      // localStorage unavailable (e.g. incognito strict mode) — fail silently
      if (!hasLoggedStorageError) {
        console.warn('useSessionActivity: could not write lastActiveAt', error);
        hasLoggedStorageError = true;
      }
    }
  }, []);

  const handleActivity = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      writeLastActiveAt();
    }, ACTIVITY_DEBOUNCE_MS);
  }, [writeLastActiveAt]);

  useEffect(() => {
    if (!isAuthenticated || isExcludedRoute) {
      console.debug('📍 useSessionActivity: skipped', { isAuthenticated, isExcludedRoute, pathname: location.pathname });
      return;
    }

    console.debug('📍 useSessionActivity: tracking started on', location.pathname);

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Write immediately on mount/route change so the timestamp is fresh
    // even before the first interaction
    writeLastActiveAt('mount');

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isAuthenticated, isExcludedRoute, handleActivity, writeLastActiveAt]);
};
